import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../apiBase';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

const card = { background: '#fff', borderRadius: 14, padding: 24, marginBottom: 20, border: '1px solid var(--sp-gray-100)' };
const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--sp-gray-200)',
    boxSizing: 'border-box', fontSize: 14, fontFamily: 'ui-monospace, Consolas, monospace',
};
const hint = { fontSize: 12, color: '#64748b', margin: '4px 0 0', lineHeight: 1.5 };
const smallBtn = {
    padding: '8px 14px', borderRadius: 8, border: '1px solid var(--sp-gray-200)', background: '#fff',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

/** Блок ключей карт и геокодеров (Geoapify, DaData) на странице «Ключи API». Ключи хранятся зашифрованными. */
function ProviderBlock({ provider, view, value, onChange, enabled, onEnabled, onRemove, onTest, testing, result }) {
    const { t } = useTranslation(['adminMaps']);
    const configured = provider === 'geoapify' ? view.geoapifyConfigured : view.dadataConfigured;
    const masked = provider === 'geoapify' ? view.geoapifyKeyMasked : view.dadataKeyMasked;
    const fromConfig = provider === 'dadata' && view.dadataFromServerConfig;

    return (
        <div style={{ marginBottom: 22 }}>
            <label style={{ fontSize: 13, fontWeight: 700 }}>{t(`adminMaps:keys.${provider}`)}</label>
            <p style={hint}>{t(`adminMaps:keys.${provider}Hint`)}</p>
            <p style={{ ...hint, color: configured ? '#15803d' : '#b45309', fontWeight: 600 }}>
                {configured ? t('adminMaps:keys.configured', { masked }) : t('adminMaps:keys.notConfigured')}
            </p>
            {fromConfig && <p style={{ ...hint, color: '#b45309' }}>{t('adminMaps:keys.dadataFromConfig')}</p>}

            <input
                type="password"
                autoComplete="new-password"
                style={{ ...inputStyle, marginTop: 8 }}
                value={value}
                placeholder={t('adminMaps:keys.placeholder')}
                onChange={e => onChange(e.target.value)}
            />

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={enabled} onChange={e => onEnabled(e.target.checked)} />
                    {t('adminMaps:keys.enabled')}
                </label>
                <button type="button" style={smallBtn} disabled={testing} onClick={onTest}>
                    {testing ? t('adminMaps:keys.testing') : t('adminMaps:keys.test')}
                </button>
                {configured && !fromConfig && (
                    <button type="button" style={{ ...smallBtn, color: '#b91c1c' }} onClick={onRemove}>
                        {t('adminMaps:keys.removeKey')}
                    </button>
                )}
            </div>

            {result && (
                <p style={{ ...hint, color: result.success ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
                    {result.message}
                    {result.sample ? ` — ${t('adminMaps:keys.sample', { value: result.sample })}` : ''}
                </p>
            )}
        </div>
    );
}

export default function MapsKeysCard() {
    const { t } = useTranslation(['adminMaps']);
    const [view, setView] = useState(null);
    const [geoKey, setGeoKey] = useState('');
    const [dadataKey, setDadataKey] = useState('');
    const [geoEnabled, setGeoEnabled] = useState(true);
    const [dadataEnabled, setDadataEnabled] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgIsError, setMsgIsError] = useState(false);
    const [testing, setTesting] = useState({});
    const [results, setResults] = useState({});

    const applyView = useCallback((v) => {
        setView(v);
        setGeoEnabled(!!v.geoapifyEnabled);
        setDadataEnabled(!!v.dadataEnabled);
        setGeoKey('');
        setDadataKey('');
    }, []);

    useEffect(() => {
        adminApi().get('/admin/maps-settings')
            .then(res => applyView(res.data))
            .catch(() => { setMsg(t('adminMaps:keys.loadError')); setMsgIsError(true); });
    }, [applyView, t]);

    async function save(extra = {}) {
        setSaving(true);
        setMsg('');
        try {
            const res = await adminApi().put('/admin/maps-settings', {
                geoapifyKey: geoKey.trim() || null,
                clearGeoapifyKey: false,
                geoapifyEnabled: geoEnabled,
                dadataKey: dadataKey.trim() || null,
                clearDadataKey: false,
                dadataEnabled,
                ...extra,
            });
            applyView(res.data);
            setMsg(t('adminMaps:keys.saved'));
            setMsgIsError(false);
        } catch {
            setMsg(t('adminMaps:keys.saveError'));
            setMsgIsError(true);
        }
        setSaving(false);
    }

    async function test(provider, key) {
        setTesting(s => ({ ...s, [provider]: true }));
        try {
            const res = await adminApi().post('/admin/maps-settings/test', { provider, key: key.trim() || null });
            setResults(r => ({ ...r, [provider]: res.data }));
        } catch (err) {
            setResults(r => ({ ...r, [provider]: { success: false, message: err.response?.data?.message || err.message } }));
        }
        setTesting(s => ({ ...s, [provider]: false }));
    }

    if (!view) {
        return msg ? <div style={card}><p style={{ color: '#c62828' }}>{msg}</p></div> : null;
    }

    return (
        <div style={card}>
            <h3 style={{ margin: '0 0 6px' }}>{t('adminMaps:keys.title')}</h3>
            <p style={{ ...hint, marginBottom: 18 }}>{t('adminMaps:keys.intro')}</p>

            <ProviderBlock
                provider="geoapify" view={view} value={geoKey} onChange={setGeoKey}
                enabled={geoEnabled} onEnabled={setGeoEnabled}
                onTest={() => test('geoapify', geoKey)} testing={!!testing.geoapify} result={results.geoapify}
                onRemove={() => { if (window.confirm(t('adminMaps:keys.removeConfirm'))) save({ clearGeoapifyKey: true }); }}
            />
            <ProviderBlock
                provider="dadata" view={view} value={dadataKey} onChange={setDadataKey}
                enabled={dadataEnabled} onEnabled={setDadataEnabled}
                onTest={() => test('dadata', dadataKey)} testing={!!testing.dadata} result={results.dadata}
                onRemove={() => { if (window.confirm(t('adminMaps:keys.removeConfirm'))) save({ clearDadataKey: true }); }}
            />

            <button
                type="button" disabled={saving} onClick={() => save()}
                style={{ padding: '12px 24px', background: '#1A1A1A', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}
            >
                {saving ? t('adminMaps:keys.saving') : t('adminMaps:keys.save')}
            </button>
            {msg && <p style={{ marginTop: 12, color: msgIsError ? '#c62828' : '#2e7d32' }}>{msg}</p>}
        </div>
    );
}
